import { FormEvent, useState, useEffect } from "react";

import SelfieImage from "@/assets/selfie.jpeg";
import { Button } from "@/components/Button/Button";
import { ImageData } from "@/requests/signup";
import { FileUploader } from "./FileUploader";

type SelfieUploadProps = {
  onConfirm: (fileData: ImageData) => void;
};

export const SelfieUpload = ({ onConfirm }: SelfieUploadProps) => {
  const [error, setError] = useState("");
  const [fileData, setFileData] = useState<ImageData | null>();

  const handleSuccess = (fileData: ImageData) => {
    setFileData(fileData);
    setError("");
  };

  const handleConfirm = (e: FormEvent) => {
    e.preventDefault();
    if (fileData) {
      onConfirm(fileData);
    }
  };

  // Check if the form is valid and ready for submission
  const isFormValid = fileData && !error;

  // Add Enter key handler
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only proceed if Enter key is pressed and all form requirements are met
      if (e.key === "Enter" && isFormValid) {
        e.preventDefault();
        // Create a synthetic event for the form submission
        const syntheticEvent = {
          preventDefault: () => {},
        } as FormEvent;
        handleConfirm(syntheticEvent);
      }
    };

    // Add event listener
    document.addEventListener("keydown", handleKeyPress);

    // Remove event listener on cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFormValid, fileData]); // Dependencies that affect form validation

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={handleConfirm}
      data-testid="form-selfie-upload"
    >
      <div data-testid="text-selfie-upload-instruction">
        Please upload a photo with the following:
      </div>
      <div className="px-4" data-testid="container-selfie-upload-requirements">
        <p
          style={{ textAlign: "left" }}
          data-testid="text-selfie-upload-description"
        >
          Clear image of yourself <b>HOLDING</b> the following 2 items
        </p>
        <div className="px-8">
          <ol
            className="list-decimal"
            data-testid="list-selfie-upload-requirements"
          >
            <li data-testid="item-selfie-upload-requirement-1">
              A clear image of your Photo ID (e.g. Drivers License/Passport)
            </li>
            <li data-testid="item-selfie-upload-requirement-2">
              A handwritten note with the words "PayTrie" as well as
              today&apos;s date
            </li>
          </ol>
        </div>
      </div>
      {error && (
        <div
          className="text-rose-600"
          data-testid="error-selfie-upload-message"
        >
          {error}
        </div>
      )}

      {fileData && (
        <div
          className="flex justify-center invisible md:visible"
          data-testid="container-selfie-upload-preview"
        >
          <img
            src={fileData.url}
            width={100}
            data-testid="img-selfie-upload-preview"
          />
        </div>
      )}
      {!fileData && (
        <div
          className="hidden md:flex justify-center"
          data-testid="container-selfie-upload-example"
        >
          <img
            src={SelfieImage}
            width={170}
            data-testid="img-selfie-upload-example"
          />
        </div>
      )}
      <FileUploader
        onUploadComplete={handleSuccess}
        onError={setError}
        customButtonText=""
        showUploadIcon={true}
        data-testid="container-selfie-upload"
      />
      {isFormValid && (
        <Button
          type="submit"
          disabled={!isFormValid}
          data-testid="btn-selfie-upload-confirm"
        >
          Confirm
        </Button>
      )}
    </form>
  );
};
